import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TimepickerModule } from 'ngx-bootstrap/timepicker';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { CalendarService } from 'src/app/calendar/services/calendar.service';
import { CalendarEventEntry } from 'src/app/shared/services/local-database.service';
import { Customer } from 'src/app/shared/models/accounting';
import { ContactTypes, ModalType } from 'src/app/shared/models/constants';
import { Subject, takeUntil, filter, take } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';


@Component({
  selector: 'app-add-edit-event',
  standalone: true,
  templateUrl: './add-edit-event.component.html',
  styleUrl: './add-edit-event.component.css',
  encapsulation: ViewEncapsulation.None,
  imports: [ReactiveFormsModule, NgSelectModule, BsDatepickerModule, TimepickerModule],
})
export class AddEditEventComponent implements OnInit {
  event_form: FormGroup;
  event_data: any; // Passed from modal service (contains { date, event })
  customers: Customer[] = [];
  destroy$ = new Subject<void>();
  isEditMode = false;
  originalEventIndex = -1;
  minDate = new Date();

  get selectedDate(): string {
    const raw = this.event_data?.event?.start ?? this.event_data?.date;
    if (!raw) return '';
    return new Date(raw).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  }
  eventTitles = ['FFL Transfer', 'NFA Transfer', 'Firearm Repair'];
  durations = [
    { label: '15 Minutes', value: 15 },
    { label: '30 Minutes', value: 30 },
    { label: '45 Minutes', value: 45 },
    { label: '1 Hour', value: 60 },
    { label: '1.5 Hours', value: 90 },
    { label: '2 Hours', value: 120 },
    { label: '3 Hours', value: 180 },
    { label: '4 Hours', value: 240 },
  ];

  constructor(
    private fb: FormBuilder,
    public bsModalRef: BsModalRef,
    public accountingService: AccountingService,
    private modalService: ModalService,
    private sharedService: SharedService,
    private calendarService: CalendarService
  ) {
    this.event_form = this.fb.group({
      title: ['', Validators.required],
      event_date: [new Date(), Validators.required],
      begin_time: [new Date(), Validators.required],
      duration: [30, Validators.required], // in minutes
      customer_id: [null],
      description: [''],
      send_reminder: [true]
    });
  }

  ngOnInit(): void {
    const existingEvent = this.event_data?.event;

    if (existingEvent) {
      // EDIT MODE: pre-fill from the existing FullCalendar event
      this.isEditMode = true;
      const events = this.sharedService.calendar_events$.value;
      this.originalEventIndex = events.findIndex(
        (e) => e.id === existingEvent.id
      );
      const start = new Date(existingEvent.start);
      const end = new Date(existingEvent.end);
      const durationMs = end.getTime() - start.getTime();
      
      this.event_form.patchValue({
        title: existingEvent.title,
        event_date: start,
        begin_time: start,
        duration: Math.round(durationMs / 60000),
        customer_id: existingEvent.extendedProps?.customer_id ?? null,
        description: existingEvent.extendedProps?.description ?? '',
        send_reminder: existingEvent.extendedProps?.send_reminder ?? false
      });
    } else if (this.event_data?.date) {
      // ADD MODE: default to 9AM on clicked date
      let date = new Date(this.event_data.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Don't allow selecting dates in the past
      if (date < today) {
        date = new Date();
      }

      const time = new Date(date);
      time.setHours(9, 0, 0);
      
      this.event_form.patchValue({
        title: 'FFL Transfer',
        event_date: date,
        begin_time: time
      });
    }

    this.event_form.get('customer_id')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        const reminderControl = this.event_form.get('send_reminder');
        if (!this.hasCustomerPhone) {
          reminderControl?.disable();
          reminderControl?.patchValue(false);
        } else {
          reminderControl?.enable();
          reminderControl?.patchValue(true);
        }
      });

    this.loadCustomers();
    
    // Initial check for edit mode
    setTimeout(() => {
      if (!this.hasCustomerPhone) {
        this.event_form.get('send_reminder')?.disable();
      }
    });

    // Listen for new customers added while this modal is open
    this.accountingService.customer_added$
      .pipe(takeUntil(this.destroy$))
      .subscribe((newCustomer) => {
        this.loadCustomers();
        this.event_form.patchValue({ customer_id: newCustomer.id });
      });
  }

  get hasCustomerPhone(): boolean {
    const customerId = this.event_form.get('customer_id')?.value;
    if (!customerId) return false;
    const customer = this.customers.find(c => c.id === customerId);
    return !!customer?.contact?.phone_number;
  }

  loadCustomers() {
    this.customers = this.accountingService.customers;
    if (this.customers.length === 0) {
      this.accountingService.getCustomers();
    }
  }

  addNewCustomer() {
    this.modalService.openAddEditContactModal(
      ModalType.add,
      ContactTypes.customer
    );
  }

  async saveEvent() {
    if (this.event_form.valid) {
      const formValue = this.event_form.value;

      // Combine date and time
      const date = new Date(formValue.event_date);
      const time = new Date(formValue.begin_time);
      
      const start = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        time.getHours(),
        time.getMinutes()
      );
      
      const end = new Date(start.getTime() + formValue.duration * 60000);

      const payload = {
        title: formValue.title,
        start: start.toISOString(),
        end: end.toISOString(),
        customer_id: formValue.customer_id ?? null,
        description: formValue.description ?? '',
        send_reminder: formValue.send_reminder
      };

      let savedEvent: CalendarEventEntry;
      if (this.isEditMode && this.originalEventIndex >= 0) {
        const existingId = this.sharedService.calendar_events$.value[this.originalEventIndex]?.id;
        if (existingId) {
          savedEvent = { ...payload, id: existingId } as CalendarEventEntry;
          await this.calendarService.updateEvent(existingId, payload);
        } else {
          savedEvent = { ...payload, id: uuidv4() } as CalendarEventEntry;
        }
        this.sharedService.raiseSuccessToast('Event updated successfully');
      } else {
        savedEvent = await this.calendarService.addEvent(payload);
        this.sharedService.raiseSuccessToast('Event scheduled successfully');
      }

      // Handle SMS Invitation if requested
      if (payload.send_reminder && payload.customer_id) {
        const customer = this.customers.find(c => c.id === payload.customer_id);
        if (customer?.contact?.phone_number) {
          try {
            await this.calendarService.sendInvitation(savedEvent, customer.contact.phone_number);
            this.sharedService.raiseSuccessToast('Text invitation sent to customer');
          } catch (error) {
            this.sharedService.raiseErrorToast('Failed to send text invitation');
          }
        }
      }

      // Refresh shared calendar events from local cache
      const events = await this.calendarService.getEvents();
      this.sharedService.calendar_events$.next(
        events.map(e => ({ id: e.id, title: e.title, start: e.start, end: e.end, extendedProps: { customer_id: e.customer_id, description: e.description, send_reminder: e.send_reminder } }))
      );

      this.bsModalRef.hide();
    }
  }

  async deleteEvent() {
    if (!this.isEditMode || this.originalEventIndex < 0) return;

    const existingId = this.sharedService.calendar_events$.value[this.originalEventIndex]?.id;
    if (!existingId) return;

    const question = 'Are you sure you want to delete this event?';
    this.modalService.openYesNoModal(question);

    this.sharedService.yes_no_submit$
      .pipe(
        filter(res => res.question === question),
        take(1)
      )
      .subscribe(async (res) => {
        if (res.answer === 'Yes') {
          try {
            await this.calendarService.deleteEvent(existingId);
            
            // Refresh shared calendar events
            const events = await this.calendarService.getEvents();
            this.sharedService.calendar_events$.next(
              events.map(e => ({ 
                id: e.id, 
                title: e.title, 
                start: e.start, 
                end: e.end, 
                extendedProps: { 
                  customer_id: e.customer_id, 
                  description: e.description, 
                  send_reminder: e.send_reminder 
                } 
              }))
            );
            
            this.sharedService.raiseSuccessToast('Event deleted');
            this.bsModalRef.hide();
          } catch (error) {
            this.sharedService.raiseErrorToast('Failed to delete event');
          }
        }
      });
  }

  close() {
    this.bsModalRef.hide();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
